import { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext, apiClient } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { 
  ArrowLeft, 
  MapPin, 
  Calendar, 
  Trophy, 
  Users, 
  UserPlus, 
  UserMinus,
  Activity,
  Target,
  Award,
  Clock,
  Shield,
  Ban,
  CheckCircle,
  UserCheck,
  Loader2,
  Lock,
  Home,
  Phone,
  Mail,
  AlertTriangle,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { logEngagementEvent } from '../utils/engagementTracker';
import Footer from '../components/Footer';
import { useSafeBack } from '../hooks/useSafeBack';
import PlayerFollowButton from '../components/PlayerFollowButton';
import { resolveAvatarUrl } from '../lib/avatarUrl';
import AdminPushDiagnostics from '../components/AdminPushDiagnostics';

const PublicProfile = () => {
  const { username } = useParams();
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const { goBack } = useSafeBack('/dashboard');
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdminActionModal, setShowAdminActionModal] = useState(false);
  const [adminActionType, setAdminActionType] = useState(null); // 'disable' or 'enable'
  const [adminActionReason, setAdminActionReason] = useState('');

  useEffect(() => {
    fetchProfile();
  }, [username]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/public/users/${username}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      if (error.response?.status === 404) {
        toast.error('User not found');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load profile');
      }
    } finally {
      setLoading(false);
    }
  };

  // Callback when PlayerFollowButton changes state — sync follower count
  const handleFollowChange = (userId, newState) => {
    if (!profile) return;
    const wasFollowing = profile.is_following;
    const nowFollowing = newState.following;
    
    // Log engagement
    if (nowFollowing && !wasFollowing) {
      logEngagementEvent('user_followed', {
        target_user_id: profile.id,
        target_username: profile.username,
        was_request: newState.requested
      });
    } else if (!nowFollowing && wasFollowing) {
      logEngagementEvent('user_unfollowed', {
        target_user_id: profile.id,
        target_username: profile.username
      });
    }
    
    // Update profile state to keep follower count in sync
    setProfile(prev => ({
      ...prev,
      is_following: nowFollowing,
      request_pending: newState.requested,
      follower_count: nowFollowing && !wasFollowing 
        ? prev.follower_count + 1 
        : !nowFollowing && wasFollowing 
          ? prev.follower_count - 1 
          : prev.follower_count
    }));
  };

  // Handle accepting a follow request from this user
  const [acceptLoading, setAcceptLoading] = useState(false);
  const handleAcceptFollowRequest = async () => {
    if (!profile.request_id_from_them) return;
    
    try {
      setAcceptLoading(true);
      await apiClient.post(`/follow/requests/${profile.request_id_from_them}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update profile state
      setProfile(prev => ({
        ...prev,
        request_id_from_them: null,
      }));
      
      toast.success(`Accepted follow request from ${profile.username}`);
      
      // Refresh to get updated counts
      fetchProfile();
    } catch (error) {
      console.error('Accept follow request failed:', error);
      toast.error('Failed to accept follow request');
    } finally {
      setAcceptLoading(false);
    }
  };

  // Admin action handlers
  const handleAdminAction = async () => {
    if (!profile?.id) return;
    
    try {
      const endpoint = adminActionType === 'disable' 
        ? `/admin/users/${profile.id}/disable`
        : `/admin/users/${profile.id}/enable`;
      
      await apiClient.post(endpoint, null, {
        params: { reason: adminActionReason || undefined }
      });
      
      toast.success(adminActionType === 'disable' 
        ? `User ${profile.username} has been disabled`
        : `User ${profile.username} has been enabled`
      );
      
      setShowAdminActionModal(false);
      setAdminActionReason('');
      setAdminActionType(null);
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Admin action failed');
    }
  };

  const openAdminAction = (type) => {
    setAdminActionType(type);
    setAdminActionReason('');
    setShowAdminActionModal(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getEventDescription = (event) => {
    const eventDescriptions = {
      'app_load': 'Opened the app',
      'profile_view': 'Viewed their profile',
      'profile_update': 'Updated their profile',
      'avatar_upload': 'Changed profile picture',
      'avatar_delete': 'Removed profile picture',
      'game_view': 'Viewed a game',
      'game_create': 'Created a game',
      'game_join': 'Joined a game',
      'address_update': 'Updated their address',
      'profile_bio_update': 'Updated their bio',
      'date_of_birth_update': 'Updated date of birth',
      'address_autocomplete_used': 'Updated location',
    };
    
    return eventDescriptions[event.event_type] || event.event_type;
  };

  const getSkillBadgeColor = (skill) => {
    const colors = {
      'Elite': 'bg-purple-500 text-white',
      'Baller': 'bg-orange-500 text-white',
      'Role Player': 'bg-blue-500 text-white',
      'Unranked': 'bg-gray-400 text-white'
    };
    return colors[skill] || 'bg-gray-400 text-white';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 pb-20" data-testid="profile-skeleton">
        <div className="max-w-6xl mx-auto space-y-6 animate-pulse">
          {/* Back button */}
          <div className="h-5 bg-gray-200 rounded w-16" />

          {/* Profile card */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            {/* Avatar + name */}
            <div className="flex items-center gap-4 mb-6">
              <div className="w-20 h-20 bg-gray-200 rounded-full" />
              <div className="flex-1">
                <div className="h-6 bg-gray-200 rounded w-36 mb-2" />
                <div className="h-4 bg-gray-200 rounded w-24 mb-1" />
                <div className="h-3 bg-gray-200 rounded w-48" />
              </div>
            </div>
            {/* Follow button */}
            <div className="h-10 bg-gray-200 rounded-xl w-full mb-4" />
            {/* Bio */}
            <div className="h-4 bg-gray-200 rounded w-full mb-2" />
            <div className="h-4 bg-gray-200 rounded w-3/4" />
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[0, 1, 2, 3, 4, 5].map(i => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col items-center">
                <div className="w-8 h-8 bg-gray-200 rounded-full mb-2" />
                <div className="h-6 bg-gray-200 rounded w-10 mb-1" />
                <div className="h-3 bg-gray-200 rounded w-20" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 pb-20">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Back Button + Home */}
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            onClick={goBack}
            className="flex items-center gap-2"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/dashboard')}
            data-testid="nav-home-btn"
            aria-label="Dashboard"
          >
            <Home className="h-4 w-4 text-gray-500" />
          </Button>
        </div>

        {/* Header Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
              {/* Avatar */}
              <div className="relative">
                {profile.avatar_url ? (
                  <img
                    src={resolveAvatarUrl(profile.avatar_url)}
                    alt={profile.username}
                    className="w-32 h-32 rounded-full object-cover border-4 border-orange-500"
                  />
                ) : (
                  <div className="w-32 h-32 rounded-full bg-orange-500 flex items-center justify-center border-4 border-orange-600">
                    <span className="text-4xl text-white font-bold">
                      {profile.username[0].toUpperCase()}
                    </span>
                  </div>
                )}
                {profile.is_certified_elite && (
                  <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-purple-600 text-white">
                      <Award className="h-3 w-3 mr-1" />
                      Verified
                    </Badge>
                  </div>
                )}
              </div>

              {/* Profile Info */}
              <div className="flex-1 text-center md:text-left">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900" data-testid="public-profile-display-name">
                      {profile.display_name || profile.username}
                    </h1>
                    {profile.display_name && profile.display_name !== profile.username && (
                      <p className="text-sm text-gray-500" data-testid="public-profile-handle">
                        @{profile.username}
                      </p>
                    )}
                    
                    {/* Skill Badges */}
                    <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mt-2">
                      {profile.is_private_profile && (
                        <Badge className="bg-gray-500 text-white">
                          <Lock className="h-3 w-3 mr-1" />
                          Private
                        </Badge>
                      )}
                      {profile.certified_skill_level && (
                        <Badge className={getSkillBadgeColor(profile.certified_skill_level)}>
                          <Award className="h-3 w-3 mr-1" />
                          {profile.certified_skill_level} (Certified)
                        </Badge>
                      )}
                      <Badge className={getSkillBadgeColor(profile.self_rated_skill)}>
                        <Target className="h-3 w-3 mr-1" />
                        {profile.self_rated_skill} (Self-Rated)
                      </Badge>
                    </div>

                    {/* Location (no age displayed) */}
                    <div className="flex items-center justify-center md:justify-start gap-4 mt-3 text-gray-600">
                      {(profile.city || profile.state) && (
                        <span className="flex items-center gap-1 text-sm">
                          <MapPin className="h-4 w-4" />
                          {profile.city}{profile.city && profile.state ? ', ' : ''}{profile.state}
                        </span>
                      )}
                    </div>

                    {/* Member Since */}
                    {profile.created_at && (
                      <div className="flex items-center justify-center md:justify-start gap-1 mt-2 text-gray-500 text-sm">
                        <Calendar className="h-4 w-4" />
                        Member since {formatDate(profile.created_at)}
                      </div>
                    )}
                  </div>

                  {/* Follow Button — uses shared PlayerFollowButton for consistency */}
                  {!profile.is_own_profile && (
                    <div className="flex gap-2">
                      {/* Accept Follow Request Button (if they sent us a request) */}
                      {profile.request_id_from_them && (
                        <Button
                          onClick={handleAcceptFollowRequest}
                          disabled={acceptLoading}
                          className="bg-orange-500 hover:bg-orange-600"
                          data-testid="accept-follow-request-btn"
                        >
                          {acceptLoading ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <UserCheck className="h-4 w-4 mr-2" />
                          )}
                          Accept Request
                        </Button>
                      )}
                      
                      {/* Main Follow/Requested/Following Button */}
                      <PlayerFollowButton
                        userId={profile.id}
                        username={profile.username}
                        isFollowing={profile.is_following || false}
                        isRequested={profile.request_pending || false}
                        onFollowChange={handleFollowChange}
                        variant="profile"
                        size="md"
                      />
                    </div>
                  )}
                  
                  {profile.is_own_profile && (
                    <Button
                      onClick={() => navigate('/profile')}
                      className="bg-orange-500 hover:bg-orange-600"
                    >
                      Edit Profile
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Bio Section */}
            {profile.bio && (
              <>
                <Separator className="my-6" />
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">About</h3>
                  <p className="text-gray-700">{profile.bio}</p>
                </div>
              </>
            )}

            {/* Admin Actions */}
            {user && ['admin', 'scout'].includes(user.role) && (
              <>
                <Separator className="my-6" />
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Shield className="w-5 h-5 text-purple-600" />
                    <span className="font-semibold text-purple-900">Admin Actions</span>
                  </div>
                  
                  {profile.is_disabled && (
                    <div className="mb-3 p-2 bg-red-100 rounded text-sm text-red-700">
                      This user is currently disabled
                    </div>
                  )}
                  
                  {profile.is_own_profile && (
                    <div className="mb-3 p-2 bg-yellow-100 rounded text-sm text-yellow-700">
                      This is your own profile
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    {profile.is_disabled ? (
                      <Button
                        onClick={() => openAdminAction('enable')}
                        className="bg-orange-500 hover:bg-orange-600 text-white"
                        data-testid="admin-enable-user-btn"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Enable User
                      </Button>
                    ) : (
                      <Button
                        onClick={() => openAdminAction('disable')}
                        className="bg-red-500 hover:bg-red-600 text-white"
                        data-testid="admin-disable-user-btn"
                      >
                        <Ban className="w-4 h-4 mr-2" />
                        Disable User
                      </Button>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Admin Contact & Notification Controls */}
            {user && ['admin', 'scout'].includes(user.role) && profile && (
              <AdminContactControls userId={profile.id} username={profile.username} />
            )}
          </CardContent>
        </Card>

        {user?.role === 'admin' && profile?.username?.toLowerCase() === username?.toLowerCase() && <AdminPushDiagnostics userId={profile.id} targetUsername={username} />}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="pt-6 text-center">
              <Trophy className="h-8 w-8 mx-auto mb-2 text-orange-500" />
              <div className="text-2xl font-bold text-gray-900">
                {profile.total_games_played}
              </div>
              <div className="text-sm text-gray-600">Games Played</div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6 text-center">
              <Target className="h-8 w-8 mx-auto mb-2 text-blue-500" />
              <div className="text-2xl font-bold text-gray-900">
                {profile.total_games_created}
              </div>
              <div className="text-sm text-gray-600">Games Created</div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6 text-center">
              <Activity className="h-8 w-8 mx-auto mb-2 text-green-500" />
              <div className="text-2xl font-bold text-gray-900">
                {profile.total_games_joined}
              </div>
              <div className="text-sm text-gray-600">Games Joined</div>
            </CardContent>
          </Card>

          <Card 
            className={profile.can_view_social_graph ? 'cursor-pointer hover:bg-gray-50 transition-colors' : ''}
            onClick={() => profile.can_view_social_graph && navigate(`/player/${profile.username}/followers`)}
          >
            <CardContent className="pt-6 text-center">
              <Users className="h-8 w-8 mx-auto mb-2 text-purple-500" />
              <div className="text-2xl font-bold text-gray-900">
                {profile.follower_count}
              </div>
              <div className="text-sm text-gray-600">
                Followers
                {profile.can_view_social_graph && <span className="text-xs text-gray-400 ml-1">(tap to view)</span>}
              </div>
            </CardContent>
          </Card>

          <Card 
            className={profile.can_view_social_graph ? 'cursor-pointer hover:bg-gray-50 transition-colors' : ''}
            onClick={() => profile.can_view_social_graph && navigate(`/player/${profile.username}/following`)}
          >
            <CardContent className="pt-6 text-center">
              <Users className="h-8 w-8 mx-auto mb-2 text-indigo-500" />
              <div className="text-2xl font-bold text-gray-900">
                {profile.following_count}
              </div>
              <div className="text-sm text-gray-600">
                Following
                {profile.can_view_social_graph && <span className="text-xs text-gray-400 ml-1">(tap to view)</span>}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6 text-center">
              <Clock className="h-8 w-8 mx-auto mb-2 text-gray-500" />
              <div className="text-sm font-semibold text-gray-900">
                {formatDate(profile.last_active_at)}
              </div>
              <div className="text-sm text-gray-600">Recorded activity or signup date</div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        {profile.recent_activity && profile.recent_activity.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {profile.recent_activity.map((event) => (
                  <div key={`activity-${event.timestamp}-${event.game_id || event.type}`} className="flex items-start gap-3 pb-3 border-b last:border-b-0">
                    <div className="w-2 h-2 rounded-full bg-orange-500 mt-2"></div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-700">
                        {getEventDescription(event)}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatDate(event.timestamp)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      <Footer variant="slim" />

      {/* Admin Action Modal */}
      {showAdminActionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="bg-white p-6 max-w-md w-full">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-6 h-6 text-purple-600" />
              <h2 className="text-2xl font-bold text-gray-900">
                {adminActionType === 'disable' ? 'Disable User' : 'Enable User'}
              </h2>
            </div>
            <p className="text-gray-600 mb-4">
              {adminActionType === 'disable' 
                ? `You are about to disable ${profile?.username}. They will not be able to log in, create runs, or join runs.`
                : `You are about to re-enable ${profile?.username}.`
              }
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason (optional)
              </label>
              <textarea
                value={adminActionReason}
                onChange={(e) => setAdminActionReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
                rows="3"
                placeholder="Reason for this action..."
              />
            </div>
            <div className="flex gap-3">
              <Button
                onClick={handleAdminAction}
                className={adminActionType === 'disable' 
                  ? 'flex-1 bg-red-600 hover:bg-red-700 text-white'
                  : 'flex-1 bg-orange-500 hover:bg-orange-600 text-white'
                }
              >
                {adminActionType === 'disable' ? 'Disable User' : 'Enable User'}
              </Button>
              <Button
                onClick={() => {
                  setShowAdminActionModal(false);
                  setAdminActionReason('');
                  setAdminActionType(null);
                }}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

// Admin Contact & Notification Controls - inline component
const AdminContactControls = ({ userId, username }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [audit, setAudit] = useState([]);
  const [reason, setReason] = useState('');
  const [expanded, setExpanded] = useState(false);

  // Editable state
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [gameActivitySms, setGameActivitySms] = useState(true);
  const [remindersSms, setRemindersSms] = useState(true);
  const [importantSms, setImportantSms] = useState(true);
  const [allowlistBypass, setAllowlistBypass] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/admin/users/${userId}/contact-controls`);
      const d = res.data;
      setData(d);
      setEmail(d.contact.email || '');
      setPhone(d.contact.phone_number || '');
      setSmsOptIn(d.sms_preferences.sms_opt_in);
      setGameActivitySms(d.sms_preferences.game_activity_sms);
      setRemindersSms(d.sms_preferences.reminders_sms);
      setImportantSms(d.sms_preferences.important_sms);
      setAllowlistBypass(d.routing.allowlist_bypass);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const fetchAudit = async () => {
    try {
      const res = await apiClient.get(`/admin/users/${userId}/contact-audit`);
      setAudit(res.data.entries || []);
    } catch {
      // silent
    }
  };

  useEffect(() => {
    if (expanded && !data) {
      fetchData();
      fetchAudit();
    }
  }, [expanded]);

  const handleSave = async () => {
    if (!reason.trim()) { toast.error('Reason required'); return; }
    setSaving(true);
    try {
      const payload = { reason: reason.trim() };
      if (email !== data.contact.email) payload.email = email;
      if (phone !== (data.contact.phone_number || '')) payload.phone_number = phone || null;
      if (smsOptIn !== data.sms_preferences.sms_opt_in) payload.sms_opt_in = smsOptIn;
      if (gameActivitySms !== data.sms_preferences.game_activity_sms) payload.game_activity_sms = gameActivitySms;
      if (remindersSms !== data.sms_preferences.reminders_sms) payload.reminders_sms = remindersSms;
      if (importantSms !== data.sms_preferences.important_sms) payload.important_sms = importantSms;
      if (allowlistBypass !== data.routing.allowlist_bypass) payload.allowlist_bypass = allowlistBypass;
      const res = await apiClient.put(`/admin/users/${userId}/contact-controls`, payload);
      toast.success(res.data.message);
      setReason('');
      fetchData();
      fetchAudit();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = data && (
    email !== (data.contact.email || '') ||
    phone !== (data.contact.phone_number || '') ||
    smsOptIn !== data.sms_preferences.sms_opt_in ||
    gameActivitySms !== data.sms_preferences.game_activity_sms ||
    remindersSms !== data.sms_preferences.reminders_sms ||
    importantSms !== data.sms_preferences.important_sms ||
    allowlistBypass !== data.routing.allowlist_bypass
  );

  return (
    <>
      <Separator className="my-6" />
      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200" data-testid="admin-contact-controls">
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between"
          data-testid="admin-contact-toggle"
        >
          <div className="flex items-center gap-2">
            <Phone className="w-5 h-5 text-blue-600" />
            <span className="font-semibold text-blue-900">Contact & Notification Controls</span>
          </div>
          <span className="text-blue-500 text-sm">{expanded ? 'Collapse' : 'Expand'}</span>
        </button>

        {expanded && (
          <div className="mt-4 space-y-5">
            {loading ? (
              <p className="text-sm text-blue-600">Loading...</p>
            ) : data ? (
              <>
                {/* Delivery Status */}
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-zinc-500">Delivery:</span>
                  {data.routing.delivery_mode === 'production' && <Badge className="bg-green-100 text-green-800 border-green-200 text-xs">Production</Badge>}
                  {data.routing.delivery_mode === 'allowlist_bypass' && <Badge className="bg-blue-100 text-blue-800 border-blue-200 text-xs">Allowlist Bypass</Badge>}
                  {data.routing.delivery_mode === 'safe_routed' && <Badge className="bg-zinc-100 text-zinc-600 border-zinc-200 text-xs">Safe Routed</Badge>}
                </div>

                {/* Contact */}
                <div className="space-y-3">
                  <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">Contact</p>
                  <div>
                    <label className="text-xs text-zinc-500 mb-1 block">Email</label>
                    <Input value={email} onChange={e => setEmail(e.target.value)} className="h-9 text-sm" data-testid="admin-email-input" />
                  </div>
                  <div>
                    <label className="text-xs text-zinc-500 mb-1 block">Phone (E.164)</label>
                    <div className="flex items-center gap-2">
                      <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1XXXXXXXXXX" className="h-9 text-sm" data-testid="admin-phone-input" />
                      {data.contact.phone_missing && <Badge variant="outline" className="text-amber-600 border-amber-300 text-xs whitespace-nowrap"><AlertTriangle className="w-3 h-3 mr-1" />Missing</Badge>}
                      {!data.contact.phone_missing && !data.contact.phone_valid && <Badge variant="outline" className="text-red-600 border-red-300 text-xs whitespace-nowrap"><XCircle className="w-3 h-3 mr-1" />Invalid</Badge>}
                      {data.contact.phone_valid && <Badge variant="outline" className="text-green-600 border-green-300 text-xs whitespace-nowrap"><CheckCircle className="w-3 h-3 mr-1" />Valid</Badge>}
                    </div>
                  </div>
                </div>

                {/* SMS Preferences */}
                <div className="space-y-3">
                  <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">SMS Preferences</p>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-zinc-900">SMS Master Opt-In</p>
                      <p className="text-xs text-zinc-400">User-set preference</p>
                    </div>
                    <Switch checked={smsOptIn} onCheckedChange={setSmsOptIn} data-testid="admin-sms-optin-toggle" />
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-zinc-700">Game Activity</p>
                    <Switch checked={gameActivitySms} onCheckedChange={setGameActivitySms} data-testid="admin-game-activity-toggle" />
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-zinc-700">Reminders</p>
                    <Switch checked={remindersSms} onCheckedChange={setRemindersSms} data-testid="admin-reminders-toggle" />
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-zinc-700">Important Updates</p>
                    <Switch checked={importantSms} onCheckedChange={setImportantSms} data-testid="admin-important-toggle" />
                  </div>
                </div>

                {/* Routing */}
                <div className="space-y-3">
                  <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">Delivery Routing</p>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-zinc-900">Safe Mode</p>
                      <p className="text-xs text-zinc-400">Global (env)</p>
                    </div>
                    <Badge className={data.routing.safe_mode_active ? 'bg-amber-100 text-amber-700 text-xs' : 'bg-green-100 text-green-700 text-xs'}>
                      {data.routing.safe_mode_active ? 'Active' : 'Off'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-zinc-900">Allowlist Bypass</p>
                      <p className="text-xs text-zinc-400">Real delivery for this user</p>
                    </div>
                    <Switch checked={allowlistBypass} onCheckedChange={setAllowlistBypass} data-testid="admin-allowlist-toggle" />
                  </div>
                  {data.routing.safe_mode_active && !allowlistBypass && (
                    <div className="text-xs text-zinc-500 bg-zinc-100 rounded p-2">
                      SMS → {data.routing.safe_phone} · Email → {data.routing.safe_email}
                    </div>
                  )}
                </div>

                {/* Last Activity */}
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">Last Activity</p>
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-500">Last SMS</span>
                    <span className="text-zinc-800 font-medium">{data.last_sms.timestamp ? new Date(data.last_sms.timestamp).toLocaleString() : 'Never'}</span>
                  </div>
                  {data.last_sms.routing_action && (
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-500">SMS Routing</span>
                      <span className="text-zinc-600">{data.last_sms.routing_action}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-500">Last Email</span>
                    <span className="text-zinc-800 font-medium">{data.last_email.timestamp ? new Date(data.last_email.timestamp).toLocaleString() : 'Never'}</span>
                  </div>
                </div>

                {/* Save */}
                {hasChanges && (
                  <div className="p-3 bg-orange-50 rounded-lg border border-orange-200 space-y-2">
                    <p className="text-sm font-medium text-orange-800">Unsaved changes</p>
                    <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason (required for audit)" className="h-9 text-sm" data-testid="admin-reason-input" />
                    <Button onClick={handleSave} disabled={saving || !reason.trim()} className="w-full bg-orange-500 hover:bg-orange-600 text-white h-9 text-sm" data-testid="admin-save-btn">
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                )}

                {/* Audit Log */}
                {audit.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">Audit Log</p>
                    {audit.slice(0, 5).map(entry => (
                      <div key={entry.id} className="p-2 bg-white rounded border border-zinc-200 text-xs space-y-1">
                        <div className="flex justify-between text-zinc-400">
                          <span className="font-medium text-zinc-600">{entry.admin_username}</span>
                          <span>{new Date(entry.timestamp).toLocaleString()}</span>
                        </div>
                        <p className="text-zinc-500 italic">"{entry.reason}"</p>
                        {entry.changes.map((c) => (
                          <p key={`${entry.id}-${c.field}`}><span className="font-medium">{c.field}:</span> <span className="text-red-500 line-through">{String(c.old ?? 'null')}</span> → <span className="text-green-600">{String(c.new ?? 'null')}</span></p>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : null}
          </div>
        )}
      </div>
    </>
  );
};

export default PublicProfile;
